import type { CalendarDate } from "@internationalized/date";
import { useMemo, useState } from "react";
import {
  Dialog,
  ListBox,
  ListBoxItem,
  RangeCalendar,
  type Key,
  type Selection,
} from "react-aria-components";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";
import {
  CalendarDateRangeInput,
  CalendarMonth,
  CalendarNavigationHeader,
} from "./calendar.js";

/** Inclusive range of calendar days, as the calendar selects it. */
export interface CalendarDateRange {
  start: CalendarDate;
  end: CalendarDate;
}

/** One entry in the preset list beside the calendar. */
export interface DateRangePreset<V> {
  key: Key;
  textValue: string;
  label: string;
  value: V;
  checkIsSelected: (value: V) => boolean;
}

export interface DateRangePickerLayoutProps<V> {
  value: V;
  onChange: (newValue: V) => void;
  presets: readonly DateRangePreset<V>[];
  /** Earliest selectable day. */
  minCalendarValue?: CalendarDate;
  /** Latest selectable day. */
  maxCalendarValue?: CalendarDate;
  /** Which end of the selection the calendar pages to on open. */
  selectionAlignment: "start" | "end";
  /**
   * The days a value covers. Also seeds the typed entry and the focused month
   * while a preset is selected, so it must be referentially stable.
   */
  convertToCalendarValue: (value: V) => CalendarDateRange;
  /** The value for a picked range, or undefined to ignore the pick. */
  convertFromCalendarValue: (calendarValue: CalendarDateRange) => V | undefined;
}

/**
 * Presets on the left, two months of days, and typed entry beside Apply. A
 * preset applies immediately; a calendar range applies on Apply. Shared by the
 * backward and forward range dialogs, which differ only in their value type,
 * presets and selectable days.
 */
export const DateRangePickerLayout = <V,>(
  props: DateRangePickerLayoutProps<V>,
) => {
  const {
    presets,
    minCalendarValue,
    maxCalendarValue,
    selectionAlignment,
    convertToCalendarValue,
    convertFromCalendarValue,
  } = props;
  const [value, setValue] = useState<V>(props.value);
  const [isChanged, setIsChanged] = useState(false);

  const selectedPreset = presets.find((preset) =>
    preset.checkIsSelected(value),
  );
  const selectedPresetKey = selectedPreset?.key;
  const selectedPresetKeys = useMemo(
    () => (selectedPresetKey ? [selectedPresetKey] : []),
    [selectedPresetKey],
  );

  const handlePresetChange = (keys: Selection, close: () => void) => {
    const key = keys !== "all" ? keys.values().next().value : undefined;
    const preset = presets.find((p) => p.key === key);
    if (preset) {
      props.onChange(preset.value);
      close();
    }
  };

  const calendarValue = useMemo(
    () => convertToCalendarValue(value),
    [convertToCalendarValue, value],
  );

  // The calendar disables days outside the range, but the typed entry only
  // marks them invalid and still reports them, so Apply has to check.
  const isWithinCalendarRange = [calendarValue.start, calendarValue.end].every(
    (date) =>
      (minCalendarValue === undefined || date.compare(minCalendarValue) >= 0) &&
      (maxCalendarValue === undefined || date.compare(maxCalendarValue) <= 0),
  );
  const canApply = isChanged && isWithinCalendarRange;

  const handleCalendarChange = (newCalendarValue: CalendarDateRange) => {
    const newValue = convertFromCalendarValue(newCalendarValue);
    if (newValue !== undefined) {
      setValue(newValue);
      setIsChanged(true);
    }
  };

  const handleApplyClick = (close: () => void) => {
    props.onChange(value);
    close();
  };

  return (
    <Dialog className="grid grid-cols-[auto_auto] grid-rows-[auto_auto]">
      {({ close }) => (
        <>
          <div className="row-span-full border-r border-border-light py-1">
            <ListBox
              aria-label="Preset date ranges"
              className="text-sm"
              selectionMode="single"
              selectedKeys={selectedPresetKeys}
              onSelectionChange={(selection) =>
                handlePresetChange(selection, close)
              }
            >
              {presets.map((preset) => (
                <ListBoxItem
                  id={preset.key}
                  key={preset.key}
                  textValue={preset.textValue}
                  className="py-2 pr-6 pl-3 text-sm text-foreground select-none hover:bg-gray-100 selected:bg-primary selected:text-primary-foreground"
                >
                  {preset.label}
                </ListBoxItem>
              ))}
            </ListBox>
          </div>

          <div className="p-4">
            <RangeCalendar
              aria-label="Date range"
              visibleDuration={{ months: 2 }}
              value={selectedPreset ? null : calendarValue}
              onChange={handleCalendarChange}
              pageBehavior="single"
              minValue={minCalendarValue}
              maxValue={maxCalendarValue}
              defaultFocusedValue={
                selectionAlignment === "start"
                  ? calendarValue.start
                  : calendarValue.end
              }
              selectionAlignment={selectionAlignment}
            >
              <CalendarNavigationHeader />

              <div className="flex items-start gap-4">
                {[0, 1].map((month) => (
                  <CalendarMonth
                    key={month}
                    offsetMonths={month}
                    selectionMode="range"
                  />
                ))}
              </div>
            </RangeCalendar>
          </div>

          <div className="flex items-center justify-between gap-4 px-4 pb-4">
            <CalendarDateRangeInput
              value={{
                start: selectedPreset ? null : calendarValue.start,
                end: selectedPreset ? null : calendarValue.end,
              }}
              isRequired={!selectedPreset}
              minValue={minCalendarValue}
              maxValue={maxCalendarValue}
              onStartChange={(start) =>
                handleCalendarChange({ ...calendarValue, start })
              }
              onEndChange={(end) =>
                handleCalendarChange({ ...calendarValue, end })
              }
              // Enter only applies an edited range within the calendar's
              // days; otherwise it falls through to the dialog.
              onEnter={canApply ? () => handleApplyClick(close) : undefined}
            />

            <div className="flex items-center gap-2">
              <Button
                size={Size.MEDIUM}
                variant={ButtonVariant.Primary}
                isDisabled={!canApply}
                onClick={() => handleApplyClick(close)}
              >
                Apply
              </Button>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};
